// MARK://dhk8/storefront/lib/branding
// Tenant branding resolver para el storefront público.
//
// El storefront recibe el tenantId por URL (`/reservar/:tenantId` — UUID).
// Para mostrar el nombre comercial / logo del restaurante en la topbar y el
// heading, intentamos resolver el branding contra el servicio público.
//
// ENDPOINT (pendiente de scaffold en dhk8-platform-tenants):
//   GET /v1/dhk8-platform-tenants/v1/public/branding/by-tenant/:tenantId
//        → 200 { ok: true, data: { tenantId, displayName, logoUrl, accentColor } }
//        → 404 { ok: false, error: { code: 'NOT_FOUND', ... } } (fallback NintaiGO)
//
// Mientras el endpoint no exista (o devuelva 404), el hook degrada
// silenciosamente al fallback NintaiGO — la página NUNCA queda sin marca.
// Patrón replicado del guest: apps/dhk8-guest/src/lib/useTenantBranding.ts.

import { useEffect, useState } from 'react'

const API_BASE =
  (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env['VITE_API_BASE'] ??
  '/v1'

export interface TenantBranding {
  tenantId: string
  /** Nombre comercial visible — fallback "NintaiGO" si no hay branding. */
  displayName: string
  /** URL absoluta o relativa al logo del tenant; null = usar wordmark NintaiGO. */
  logoUrl: string | null
  /** Hex acento del tenant (#RRGGBB). null = usar `--tint` del design system. */
  accentColor: string | null
}

export const NINTAIGO_FALLBACK_BRANDING: TenantBranding = {
  tenantId: '',
  displayName: 'NintaiGO',
  logoUrl: null,
  accentColor: null,
}

// Cache module-level: una sola network call por tenantId mientras la app vive.
// Promesa cacheada → llamadas concurrentes para el mismo id comparten resultado.
const cache = new Map<string, Promise<TenantBranding | null>>()

function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
}

async function fetchBranding(tenantId: string): Promise<TenantBranding | null> {
  if (!isUuid(tenantId)) return null
  try {
    const res = await fetch(
      `${API_BASE}/dhk8-platform-tenants/v1/public/branding/by-tenant/${encodeURIComponent(tenantId)}`,
      { headers: { accept: 'application/json' } },
    )
    if (!res.ok) return null
    const body = (await res.json()) as
      | { ok: true; data: Partial<TenantBranding> & { tenantId: string } }
      | { ok: false }
    if (!('ok' in body) || !body.ok) return null
    const d = body.data
    return {
      tenantId: d.tenantId,
      displayName: typeof d.displayName === 'string' && d.displayName.trim() ? d.displayName : 'NintaiGO',
      logoUrl: typeof d.logoUrl === 'string' && d.logoUrl ? d.logoUrl : null,
      accentColor: typeof d.accentColor === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(d.accentColor) ? d.accentColor : null,
    }
  } catch {
    return null
  }
}

function getBranding(tenantId: string): Promise<TenantBranding | null> {
  const existing = cache.get(tenantId)
  if (existing) return existing
  const p = fetchBranding(tenantId)
  cache.set(tenantId, p)
  return p
}

/**
 * Hook React: resuelve branding por tenantId y aplica el accent color al
 * <html> (vía custom property `--brand-accent`). Devuelve siempre un valor
 * válido — si la llamada falla o el endpoint aún no existe, retorna el
 * fallback NintaiGO con `tenantId` poblado.
 */
export function useTenantBranding(tenantId: string | null | undefined): TenantBranding {
  const [branding, setBranding] = useState<TenantBranding>(() => ({
    ...NINTAIGO_FALLBACK_BRANDING,
    tenantId: tenantId ?? '',
  }))

  useEffect(() => {
    if (!tenantId || !isUuid(tenantId)) {
      setBranding({ ...NINTAIGO_FALLBACK_BRANDING, tenantId: tenantId ?? '' })
      return
    }
    let alive = true
    void getBranding(tenantId).then((b) => {
      if (!alive) return
      if (b) setBranding(b)
      else setBranding({ ...NINTAIGO_FALLBACK_BRANDING, tenantId })
    })
    return () => {
      alive = false
    }
  }, [tenantId])

  // Aplicar accentColor a `--brand-accent` (que NintaiGO theme usa con fallback
  // a `var(--brand)`). Cuando es null, removemos la propiedad → vuelve al verde.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (branding.accentColor) {
      root.style.setProperty('--brand-accent', branding.accentColor)
    } else {
      root.style.removeProperty('--brand-accent')
    }
  }, [branding.accentColor])

  return branding
}
